import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase.config';
import { quinielaJujuy } from './useExtractos';
import { temaDe } from './temasJuegos';

/**
 * Arma las diapositivas del carrusel del hero con datos REALES de Firestore.
 *
 * Regla de oro (N1): ninguna rama de este archivo devuelve un número, un monto
 * ni una fecha que no haya salido de la base. Si el dato no está, la
 * diapositiva se degrada de `completo` a `anuncio`, y si tampoco hay pozo ni
 * próximo sorteo, directamente no entra al carrusel (`oculto`). Nunca se
 * dibuja un casillero vacío ni un monto de relleno.
 *
 * De dónde sale cada cosa (A1: Firestore directo, nunca cloudfunctions.net):
 *
 *   extractos/{fecha}.pozosEstimados[]  → pozo y próxima fecha de cada juego
 *   extractos/{fecha}.quiniela[]        → la Tómbola Jujeña (turno + cabeza)
 *   extractos/{fecha}.pozo[]            → Pozo de la Quiniela (bolillas + Super Bola)
 *   sorteos_estado/{juegoClave}         → tramo B, todavía no existe (ver abajo)
 *
 * El conjunto de diapositivas se arma A PARTIR DE LOS DATOS, no de una lista
 * fija: el día que `pozosEstimados` traiga un juego nuevo, entra solo. Los que
 * pierden su dato salen del carrusel en vez de mostrar un hueco.
 *
 * El VESTIDO de cada diapositiva (logo, acento, tinta legible, tipografía) no
 * está acá: sale de `temasJuegos.js`, que es configuración. Este archivo
 * decide qué juegos entran y con qué datos; ese otro decide cómo se ven.
 */

const PUB = process.env.PUBLIC_URL || '';

/**
 * Tramo B. Encendido el 21/08/2026: las reglas declaran `sorteos_estado` con
 * lectura pública (ruleset 111bbe3b) y la ingesta quedó desplegada, así que los
 * documentos existen y se pueden leer sin sesión. Las diapositivas de estos dos
 * juegos pasan solas de `anuncio` a `completo`; el componente no se tocó.
 *
 * Van SOLO los dos juegos que el backend ingesta. Quini 6 y Brinco quedan
 * afuera a propósito: `functions/sorteos/parser.js` no los declara y su fuente
 * (Santa Fe) vive en el puerto 8443, cuyo egreso desde Cloud Functions todavía
 * no está confirmado. Agregarlos acá antes de tener la ingesta sería un
 * `permission-denied`… no: sería peor, un documento inexistente y una
 * diapositiva que promete un sorteo que nadie escribió.
 */
export const JUEGOS_CON_SORTEO = ['lotoplus', 'loto5plus'];

/**
 * Metadatos editoriales de los juegos que ya conocemos. NO es la lista de lo
 * que se muestra: eso lo deciden los datos. Un juego que no esté acá igual
 * entra al carrusel, con el nombre derivado de su clave y su logo homónimo.
 */
const CATALOGO = {
  tombola: { nombre: 'Tómbola Jujeña', familia: 'quiniela', etiqueta: 'Tómbola de Jujuy' },
  lotoplus: { nombre: 'Loto Plus', familia: 'bolillero', etiqueta: 'Pozo más grande' },
  quini6: { nombre: 'Quini 6', familia: 'bolillero', etiqueta: 'Seis números' },
  pozo: { nombre: 'Pozo de la Quiniela', familia: 'pozo-quiniela', etiqueta: 'Con Super Bola' },
  brinco: { nombre: 'Brinco', familia: 'bolillero', etiqueta: 'Sorteo semanal' },
  loto5plus: { nombre: 'Loto 5 Plus', familia: 'bolillero', etiqueta: 'Cinco números' },
  telekino: { nombre: 'Telekino', familia: 'carton', etiqueta: 'Cartón de 15' },
  loteria: { nombre: 'Lotería', familia: 'billete', etiqueta: 'Premio mayor' },
};

/**
 * El sitio origen tiene dos sliders distintos —`quiniela` y `tombola`— para el
 * mismo juego de la home. La clave que se usa acá es `tombola`.
 */
const ALIAS = { quiniela: 'tombola' };

/**
 * Orden fijo y declarado, NO por monto: si ordenara por pozo, el hero
 * cambiaría de orden en cada visita y la Tómbola —el juego propio de la
 * provincia— se hundiría al fondo. Se intercalan los que tienen números con
 * los que solo tienen pozo para que las primeras diapositivas vendan más.
 * Lo que no esté en la lista va al final, en el orden en que llegó.
 */
// La Tómbola abre el carrusel: es el juego propio de la provincia y el de mayor
// frecuencia —cuatro sorteos por día contra uno o dos por semana del resto—.
// Es la misma decisión editorial que la destaca en la grilla de juegos.
const ORDEN = ['tombola', 'lotoplus', 'quini6', 'pozo', 'brinco', 'loto5plus', 'telekino', 'loteria'];

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

const normalizarClave = (juego) => {
  if (typeof juego !== 'string') return null;
  const clave = juego.trim().toLowerCase();
  if (!clave) return null;
  return ALIAS[clave] || clave;
};

const titular = (clave) => clave.charAt(0).toUpperCase() + clave.slice(1);

/**
 * `pozosEstimados[].fecha` viene en DD/MM/YYYY. El día de la semana se DERIVA
 * de esa fecha, no se inventa.
 *
 * Si la fecha ya pasó, devuelve null: el hero decía "Próximo: domingo 05/08"
 * dos semanas después de ese domingo. Una fecha vencida es una promesa
 * concreta que cualquiera puede verificar y encontrar falsa, así que ante la
 * duda no se publica (N2).
 */
export function proximaFecha(valor) {
  if (typeof valor !== 'string') return null;
  const texto = valor.trim();

  // DD/MM/YYYY (pozosEstimados) o YYYY-MM-DD (sorteos_estado, tramo B).
  const barras = texto.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!barras && !iso) return null;

  const dia = Number(barras ? barras[1] : iso[3]);
  const mes = Number(barras ? barras[2] : iso[2]);
  const anio = Number(barras ? barras[3] : iso[1]);
  const fecha = new Date(anio, mes - 1, dia);
  if (Number.isNaN(fecha.getTime()) || fecha.getDate() !== dia) return null;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const dias = Math.round((fecha.getTime() - hoy.getTime()) / 86400000);
  if (dias < 0) return null;

  const corta = `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}`;
  if (dias === 0) return { cuando: 'Hoy', fecha: corta };
  if (dias === 1) return { cuando: 'Mañana', fecha: corta };
  return { cuando: DIAS[fecha.getDay()], fecha: corta };
}

/** El pozo del Pozo de la Quiniela llega como string sin normalizar ("39.740.000"). */
const comoTexto = (valor) => {
  if (typeof valor !== 'string') return null;
  const limpio = valor.trim();
  if (!limpio) return null;
  return limpio.startsWith('$') ? limpio : `$${limpio}`;
};

function armarSlides(extractos, estados) {
  const porClave = new Map();

  const asegurar = (clave) => {
    if (!porClave.has(clave)) {
      const meta = CATALOGO[clave] || {};
      // El vestido del juego sale de `temasJuegos.js`, que es configuración:
      // logo, acento, tinta legible y tipografía. Un juego sin entrada cae al
      // tema de la casa y a su logo homónimo, y se ve bien igual.
      const tema = temaDe(clave, PUB);
      porClave.set(clave, {
        juegoClave: clave,
        nombre: meta.nombre || titular(clave),
        familia: meta.familia || 'bolillero',
        etiqueta: meta.etiqueta || null,
        logo: tema.logo,
        acento: tema.acento,
        tinta: tema.tinta,
        fuente: tema.fuente,
        escLogo: tema.esc,
        platoLogo: tema.plato,
        pozoMonto: null,
        pozoTexto: null,
        pozoDudoso: false,
        fuentePozo: null,
        proximo: null,
        numeros: null,
        cabeza: null,
        superBola: null,
        plus: null,
        modalidad: null,
        turno: null,
        sorteoNro: null,
        fecha: null,
        caduca: null,
      });
    }
    return porClave.get(clave);
  };

  // 1. Pozos estimados: es lo que hace que un juego exista en el carrusel.
  (extractos?.pozosEstimados || []).forEach((p) => {
    // `juego` puede venir null si el sitio origen cambió la clase del slider.
    // Sin clave no hay juego que mostrar: se descarta en vez de adivinar.
    const clave = normalizarClave(p?.juego);
    if (!clave) return;

    const s = asegurar(clave);
    if (typeof p.monto === 'number' && Number.isFinite(p.monto) && p.monto > 0) {
      s.pozoMonto = p.monto;
    } else {
      s.pozoTexto = comoTexto(p.montoTexto);
    }
    if (s.pozoMonto || s.pozoTexto) s.fuentePozo = 'loterialajujeña.com.ar';
    s.proximo = proximaFecha(p.fecha);
  });

  // 2. Tómbola Jujeña: el último turno sorteado del día.
  const turnos = quinielaJujuy(extractos);
  const ultimo = turnos[turnos.length - 1];
  if (ultimo?.numeros?.length) {
    const s = asegurar('tombola');
    s.cabeza = ultimo.numeros[0];
    s.numeros = ultimo.numeros;
    s.turno = ultimo.turno || `Sorteo ${ultimo.turnoId}`;
    s.sorteoNro = ultimo.sorteoNro || null;
    s.fecha = extractos?.fecha || null;
  }

  // 3. Pozo de la Quiniela: bolillas + Super Bola.
  const pozo = (extractos?.pozo || [])[0];
  if (pozo) {
    const s = asegurar('pozo');
    if (pozo.numeros?.length) {
      s.numeros = pozo.numeros;
      s.superBola = pozo.superBola || null;
    }
    // `pozoEstimado` es un string sin normalizar. Se muestra tal cual vino:
    // convertirlo a número exigiría interpretar una convención de separadores
    // que no está documentada, y equivocarse ahí multiplica un pozo por cien.
    if (!s.pozoMonto) s.pozoTexto = comoTexto(pozo.pozoEstimado) || s.pozoTexto;
    s.sorteoNro = pozo.sorteoNro || null;
    s.fecha = pozo.fecha || extractos?.fecha || null;
    s.caduca = pozo.caduca || null;
  }

  // 4. Tramo B — números del último sorteo desde `sorteos_estado/{juego}`.
  //    Precedencia del pozo, escrita una sola vez y acá: gana el XML oficial
  //    (viene de la entidad que corre el sorteo y está firmado con SHA-512);
  //    el scraper de Jujuy es el respaldo. Si el XML trajo un pozo dudoso, no
  //    se muestra monto: el valor de Jujuy NO ocupa su lugar (N2).
  Object.entries(estados || {}).forEach(([clave, estado]) => {
    if (!estado) return;
    const s = asegurar(clave);

    if (estado.pozoEstimadoDudoso === true) {
      s.pozoDudoso = true;
      s.pozoMonto = null;
      s.pozoTexto = null;
      s.fuentePozo = null;
    } else if (typeof estado.pozoEstimado === 'number' && Number.isFinite(estado.pozoEstimado)) {
      s.pozoMonto = estado.pozoEstimado;
      s.pozoTexto = null;
      s.fuentePozo = estado.fuentePozo || estado.fuente || null;
    }

    const proximo = proximaFecha(estado.proximoSorteoISO);
    if (proximo) s.proximo = proximo;

    if (estado.destacada?.numeros?.length) {
      s.numeros = estado.destacada.numeros;
      s.modalidad = estado.destacada.nombre || null;
    }
    if (typeof estado.plus === 'string' && estado.plus) s.plus = estado.plus;
    if (typeof estado.sorteoNro === 'number') s.sorteoNro = estado.sorteoNro;
    if (typeof estado.fecha === 'string' && estado.fecha) s.fecha = estado.fecha;
  });

  const conModo = [...porClave.values()].map((s) => {
    const hayNumeros = !!(s.numeros?.length || s.cabeza);
    const hayAnuncio = !!(s.pozoMonto || s.pozoTexto || s.proximo);
    return { ...s, modo: hayNumeros ? 'completo' : hayAnuncio ? 'anuncio' : 'oculto' };
  });

  // `oculto` = no entra. Antes que una tarjeta hueca, una diapositiva menos.
  const visibles = conModo.filter((s) => s.modo !== 'oculto');

  return visibles.sort((a, b) => {
    const ia = ORDEN.indexOf(a.juegoClave);
    const ib = ORDEN.indexOf(b.juegoClave);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return 0;
  });
}

/**
 * Devuelve `{ slides, cargando, clave }`.
 *
 * Los extractos entran POR PARÁMETRO, no con un `useExtractos()` propio: la
 * home ya los pide para el tablero y el ticker, y llamar al hook otra vez
 * duplicaría las dos lecturas a Firestore en cada visita.
 *
 * `clave` identifica el CONJUNTO de diapositivas: cuando cambia (llegó un
 * sorteo nuevo, se cayó un juego), el carrusel vuelve el índice a 0 en vez de
 * quedar apuntando a una posición que ya no existe.
 */
export function useCarruselJuegos(extractos, cargandoExtractos) {
  const [estados, setEstados] = useState(null);
  const [cargandoEstados, setCargandoEstados] = useState(JUEGOS_CON_SORTEO.length > 0);

  useEffect(() => {
    if (!JUEGOS_CON_SORTEO.length) return undefined;
    let vivo = true;

    (async () => {
      // Los cuatro documentos en PARALELO: son independientes y encadenarlos
      // multiplicaría por cuatro el tiempo hasta que el hero tiene datos.
      // `allSettled` y no `all`: que falte o falle uno no puede dejar el
      // carrusel entero en el esqueleto.
      const lecturas = await Promise.allSettled(
        JUEGOS_CON_SORTEO.map((clave) => getDoc(doc(db, `sorteos_estado/${clave}`)))
      );
      if (!vivo) return;

      const encontrados = {};
      lecturas.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value.exists()) {
          encontrados[JUEGOS_CON_SORTEO[i]] = r.value.data();
        } else if (r.status === 'rejected') {
          console.warn('No se pudo leer sorteos_estado:', r.reason?.message || r.reason);
        }
      });

      setEstados(encontrados);
      setCargandoEstados(false);
    })();

    return () => { vivo = false; };
  }, []);

  const slides = useMemo(() => armarSlides(extractos, estados), [extractos, estados]);
  const clave = useMemo(() => slides.map((s) => s.juegoClave).join('|'), [slides]);

  return { slides, cargando: cargandoExtractos || cargandoEstados, clave };
}
